import {
  closestCorners,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { FilterNode } from '@ofmos/shared/filter-ast';
import type { OrderBy } from '@ofmos/shared/record-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../../data/client';
import { useRecord, useRecordGroups, useUpdateRecord } from '../../../data/hooks';
import { queryKeys } from '../../../data/queryKeys';
import { isSelectableField } from '../../../fields/fieldTypeMeta';
import {
  Button,
  EmptyState,
  PanEdges,
  ScrollEdge,
  SelectMenu,
  Skeleton,
  type TagTone,
  useIsDesktop,
  usePanEdges,
  useToast,
} from '../../../ui';
import { MOTION } from '../../../ui/motion';
import { PanePinned } from '../../shell/PaneHost';
import { closedDomainOf, withEmptyGroups } from '../emptyGroups';
import { useRowSelection } from '../table/RowSelectionProvider';
import { applyMove } from './applyMove';
import {
  type BoardColumn,
  boardGroupRefusal,
  buildColumns,
  columnCounts,
  columnDot,
  columnOfRecord,
} from './boardColumns';
import { KanbanCardBody, recordTitle } from './KanbanCard';
import { KanbanColumn } from './KanbanColumn';
import { moveTarget, recordIdOfDragId } from './moveTarget';

/**
 * THE BOARD.
 *
 * Grouping is by a SELECT field, because a column set has to be a closed, ordered list
 * of known values - that is exactly what a SELECT is. There is no fallback grouping: a
 * board over free text would invent columns from typos.
 *
 * ONE QUERY PER COLUMN (record-page-and-views.md D21). It used to be one 200-row page
 * grouped on the client, and every number in every header was a count of that PAGE
 * rather than of the column - a board whose "At risk: 12" meant "12 of the 200 rows we
 * happened to fetch". D22 makes that intolerable rather than merely untidy: if Leads
 * OPENS as a board, per-column counts that are really per-page counts are the
 * spreadsheet feeling in a new costume. Each column now owns its filter, its count and
 * its cursor, and this component owns only what spans them - which column a card came
 * from, and what a drop writes.
 *
 * Mobile is the primary layout, not a narrowed desktop one. Below the desktop breakpoint
 * the columns are snap stops the width of the screen and a drag needs a long press, so a
 * swipe still scrolls the board; from `md:` up the columns sit side by side and a few
 * pixels of pointer travel start the drag.
 */
export interface KanbanViewProps {
  readonly object: ObjectMetadata;
  readonly fields: readonly FieldMetadata[];
  /**
   * The view's filter, already materialised (D17). A board is a VIEW of the same list,
   * so it narrows to the same rows the table would - which is what makes switching
   * between the two a change of shape rather than a change of subject.
   */
  readonly filter?: FilterNode | undefined;
  /**
   * WHICH FIELD THE COLUMNS COME FROM, chosen by the caller (D20). It used to be local
   * state, which is why a board could not be saved: the one thing a board view has to
   * remember lived in the component that unmounts when you leave it.
   */
  readonly groupFieldId: string | null;
  readonly onGroupFieldChange: (fieldId: string) => void;
}

/**
 * ONE VALUE UNDER THE TITLE (wave 5 round 4, B4). The spec's card is "the table row's content
 * (identifier, one secondary line, status dot) at 54 px plus the secondary line"
 * (spec/screens/list.md section 2 item 12): one LINE, and a line holds one value that can
 * truncate. Two made the card a definition list - a label column and a value column - which is
 * a table wearing a card's clothes and cost the card 42px of the 96 it stood at.
 */
const SECONDARY_FIELD_COUNT = 1;

export function KanbanView({
  object,
  fields,
  filter,
  groupFieldId: chosenGroupFieldId,
  onGroupFieldChange,
}: KanbanViewProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const isDesktop = useIsDesktop();

  const selectFields = useMemo(
    () => fields.filter((field) => field.type === 'SELECT').sort((a, b) => a.position - b.position),
    [fields],
  );

  // The chosen field is an ID, not an object: a metadata refetch replaces the objects,
  // and a board that reset its grouping on every refetch would be unusable.
  const groupField =
    selectFields.find((field) => field.id === chosenGroupFieldId) ?? selectFields[0];

  const labelField = fields.find((field) => field.id === object.labelIdentifierFieldId);
  const groupFieldId = groupField?.id;

  const secondaryFields = useMemo(
    () =>
      fields
        .filter((field) => field.id !== object.labelIdentifierFieldId && field.id !== groupFieldId)
        // 1.2: a MANY_TO_MANY in the select comes back null at HTTP 200, so a card would
        // carry a permanently empty line and the board would spend a column on nothing.
        .filter(isSelectableField)
        .sort((a, b) => a.position - b.position)
        .slice(0, SECONDARY_FIELD_COUNT),
    [fields, object.labelIdentifierFieldId, groupFieldId],
  );

  const select = useMemo(() => {
    const ids = new Set<string>();
    if (labelField !== undefined) {
      ids.add(labelField.id);
    }
    if (groupFieldId !== undefined) {
      ids.add(groupFieldId);
    }
    for (const field of secondaryFields) {
      ids.add(field.id);
    }
    return [...ids];
  }, [labelField, groupFieldId, secondaryFields]);

  const orderBy: readonly OrderBy[] | undefined =
    labelField === undefined ? undefined : [{ fieldId: labelField.id, direction: 'asc' }];

  const columns = useMemo<readonly BoardColumn[]>(
    () => (groupField === undefined ? [] : buildColumns(groupField)),
    [groupField],
  );
  const refusal = groupField === undefined ? null : boardGroupRefusal(groupField);

  /**
   * EVERY HEADER COUNT, IN ONE READ (group-by-aggregation.md D23).
   *
   * The columns keep their own row queries - rows with independent cursor paging and a
   * drop target per column is not something a `GROUP BY` returns - and give up only their
   * counts. Each one used to ask for `withTotalCount`, which is a second full scan of the
   * filter, so a seven-option board paid seven extra scans on every cache miss.
   *
   * THE `groupLimit` IS EXPLICIT AND IS `columns.length`, which is the option count plus
   * the no-value bucket. A grouped read returns at most `groupLimit` groups - 50 by
   * default - while `buildColumns` renders a column for every option always, so the
   * default would give a wide board columns with no count at all, and D20's remedy for
   * that is a truncation ROW, which is a table element with nowhere to live on a board.
   * Past `GROUP_MAX_LIMIT` the ask would be refused rather than clamped, which is
   * `boardGroupRefusal` above: a select that wide is not a board.
   */
  const counts = useRecordGroups(object.id, {
    select: groupFieldId === undefined ? [] : [groupFieldId],
    ...(filter === undefined ? {} : { filter }),
    groupBy: groupFieldId === undefined ? [] : [{ fieldId: groupFieldId }],
    groupLimit: Math.max(columns.length, 1),
    // Aggregates only: the cards come from the columns' own queries.
    rowsPerGroup: 0,
    enabled: groupFieldId !== undefined && refusal === null && columns.length > 0,
  });

  /**
   * An option with NO ROWS renders 0, and D29 is why that is legal rather than a
   * client-side zero substitution: the grouped read returns nothing for it, and the
   * synthesis puts it back as a real group whose aggregates are D12's table evaluated over
   * zero rows. The board calls the same function the grouped table does.
   */
  const countByColumn = useMemo<ReadonlyMap<string, number> | undefined>(() => {
    if (counts.data === undefined || groupField === undefined) return undefined;
    return columnCounts(withEmptyGroups(counts.data, closedDomainOf(groupField) ?? []));
  }, [counts.data, groupField]);

  /**
   * WHICH COLUMN HOLDS WHICH CARD, assembled from the columns' own answers.
   *
   * Each column knows its ids because its own query returned them; this map is the
   * union, and it is the only thing the drop path needs that no single column can
   * provide. Reported UP rather than derived DOWN from each record's value, because
   * during an optimistic move the value has already changed while the queries have not -
   * and the drop path has to agree with what the user is looking at.
   *
   * The drop path's CONTRACT is unchanged (D21): `moveTarget` still takes
   * `columnOfRecord` and `columns`, and `buildColumns` still produces the second.
   */
  const [idsByColumn, setIdsByColumn] = useState<ReadonlyMap<string, readonly string[]>>(new Map());
  const handleIds = useCallback((columnId: string, ids: readonly string[]) => {
    setIdsByColumn((current) => {
      const previous = current.get(columnId);
      // A column re-reports on every render of its own query; without this the state
      // update would schedule another render, forever.
      if (previous !== undefined && sameIds(previous, ids)) return current;
      const next = new Map(current);
      next.set(columnId, ids);
      return next;
    });
  }, []);
  const cardColumns = useMemo(() => columnOfRecord(idsByColumn), [idsByColumn]);

  /**
   * THE ORDER THE BOARD IS SHOWING, PUBLISHED TO THE SELECTION (wave 5 round 3, C3-3).
   *
   * The table does this because "extend from here to there" and "the next row down" are
   * questions about the order on screen, which only the view knows (`RowSelectionProvider`).
   * A board's order is the columns left to right and each column's own answer inside it, so
   * Shift-click across two columns takes the cards between them the way a reader reads the
   * board. Outside the list page - the states gallery, a unit test - `useRowSelection`
   * returns the inert selection and `setOrder` does nothing.
   */
  const { setOrder } = useRowSelection();
  const boardOrder = useMemo(
    () => columns.flatMap((column) => idsByColumn.get(column.id) ?? []),
    [columns, idsByColumn],
  );
  useEffect(() => {
    setOrder(boardOrder);
  }, [boardOrder, setOrder]);

  const updateRecord = useUpdateRecord(object.id);
  const [activeRecordId, setActiveRecordId] = useState<string | null>(null);
  /**
   * WHETHER THE DRAG UNDER WAY WAS STARTED FROM THE KEYBOARD, which is the one thing that
   * decides whether the drag image TRAVELS - see the overlay at the foot of this file.
   */
  const [keyboardDrag, setKeyboardDrag] = useState(false);

  /**
   * The board's own pan state (audit P2-7). Declared UP HERE with the other hooks and not
   * beside the element it belongs to, because two of this component's exits are early
   * returns - the "no select field" and the "too wide for a board" empty states - and a
   * hook after them is a hook that runs conditionally.
   */
  const boardScroller = useRef<HTMLDivElement>(null);
  const boardEdges = usePanEdges(boardScroller);

  /**
   * The dragged record's TITLE, for copy a user can recognise ("Mila Vance stays in
   * Onboarding until someone approves the move").
   *
   * Read from the canonical cache entry inside the handler rather than from a row this
   * component holds - it holds none any more, because the columns own their queries.
   * A synchronous cache read is exactly what an event handler needs and a hook is not;
   * a missing entry (evicted mid-drag) degrades to "The record" rather than to a uuid.
   */
  const queryClient = useQueryClient();
  const draggedTitle = (recordId: string | null): string => {
    if (recordId === null) return 'The record';
    const row = queryClient.getQueryData<RecordRow>(queryKeys.record(object.id, recordId));
    return row === undefined ? 'The record' : recordTitle(row, labelField);
  };

  /**
   * THE LIFTED CARD'S DOT, read off the column the card is STILL IN. The board's own map is
   * what the reader is looking at (`cardColumns`), so a card lifted out of Onboarding keeps
   * Onboarding's colour all the way across the board - which is the one thing on the overlay
   * that says where it came from.
   */
  const draggedTone = (recordId: string | null): TagTone | null => {
    if (recordId === null || groupField === undefined) return null;
    const columnId = cardColumns.get(recordId);
    const column = columns.find((candidate) => candidate.id === columnId);
    return column === undefined ? null : columnDot(column, groupField);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Touch: a long press claims the card, so a swipe belongs to the scroller
      // and the board stays usable with one thumb. Mouse: a few pixels of travel,
      // because a pointer has no second gesture and a delay would feel broken.
      activationConstraint: isDesktop ? { distance: 6 } : { delay: 250, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      // Space drags, Enter opens (see KanbanCard). dnd-kit binds both to the drag
      // by default, which would leave no key to open a record with.
      keyboardCodes: { start: ['Space'], cancel: ['Escape'], end: ['Space'] },
    }),
  );

  if (groupField === undefined || groupFieldId === undefined) {
    return (
      <EmptyState
        icon="board"
        title="This object has no select field to group by"
        description="A board's columns are the options of a select field. Add one in the field admin and the board builds itself."
        action={
          <Button
            variant="secondary"
            onClick={() => void navigate(`/settings/objects/${object.id}`)}
          >
            Open field admin
          </Button>
        }
      />
    );
  }

  /**
   * D23's boundary rule, said to the user rather than left as a board whose columns have
   * no numbers. `views/validate.ts` refuses to SAVE a board grouped this wide; the picker
   * can still offer the field, so this is where one gets refused if somebody chooses it.
   */
  if (refusal !== null) {
    return (
      <EmptyState
        icon="board"
        title="This select field is too wide for a board"
        description={refusal}
        action={
          <Button
            variant="secondary"
            onClick={() => void navigate(`/settings/objects/${object.id}`)}
          >
            Open field admin
          </Button>
        }
      />
    );
  }

  const openRecord = (recordId: string) => {
    void navigate(`/objects/${object.id}/records/${recordId}`);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveRecordId(recordIdOfDragId(event.active.id));
    setKeyboardDrag(event.activatorEvent instanceof KeyboardEvent);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const draggedId = recordIdOfDragId(event.active.id);
    setActiveRecordId(null);
    const target = moveTarget({
      activeId: event.active.id,
      overId: event.over?.id,
      columnOfRecord: cardColumns,
      columns,
    });
    if (target === null) {
      return;
    }

    applyMove({
      context: {
        target,
        groupField,
        /**
         * The title, read from the canonical cache entry the column query seeded rather
         * than from a row this component holds - it holds none any more. Missing is a
         * legitimate state (a card whose entry was evicted mid-drag), and the copy
         * degrades to "The record" rather than to a uuid.
         */
        recordTitle: draggedTitle(draggedId),
        fromColumnLabel:
          columns.find((column) => column.id === target.fromColumnId)?.label ?? 'its column',
      },
      write: updateRecord.mutate,
      toast,
    });
  };

  return (
    /* `shrink-0`, AND THE PINNED LINE IS WHY (C3-3). Inside the pane host this div is a
       column flex item, so its default `flex-shrink: 1` sized it to the HOST (746px) while
       its columns overflowed it visibly - and a sticky child can only stick inside its own
       parent's box, so the Group by line left the screen after 630px of scrolling. Taking
       the shrink away makes the div as tall as the board, which is what the host is
       scrolling in the first place. */
    <div className="flex min-h-0 shrink-0 flex-col">
      {/**
       * THE BOARD'S OWN FIXED LINE (wave 5 round 3, C3-3), and it is the table's construction
       * one view over: `PanePinned` makes it sticky INSIDE the pane host at the bar's lower
       * edge, and the host's rule is that a pinned line paints no ground of its own, so it
       * mounts the kit's HARD scroll edge. Without this the cards would pass under a line
       * that is transparent, which is the defect check (u) enumerates.
       * kit: Apple macOS 27 UI Kit.sketch / Kit/Scroll Edge Effect/Dark - Top - Hard.
       * hig: https://developer.apple.com/design/human-interface-guidelines/scroll-views -
       * one effect per view, and the hard style is the macOS one for a pinned header.
       */}
      <PanePinned>
        <ScrollEdge host="board-head" style="hard" />
        <div className="flex flex-wrap items-center gap-3 py-3">
          <span className="text-2xs font-semibold tracking-widest text-ink-muted uppercase">
            Group by
          </span>
          <div className="w-56">
            <SelectMenu
              value={groupFieldId}
              onValueChange={onGroupFieldChange}
              options={selectFields.map((field) => ({ value: field.id, label: field.label }))}
              ariaLabel="Group by"
            />
          </div>
          {/*
            THE "showing the first 200 records" NOTICE IS GONE, and its absence is the
            feature: it existed because one page fed every column. Each column pages itself
            now and says so in its own footer, next to the count it belongs to.
          */}
        </div>
      </PanePinned>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveRecordId(null)}
      >
        {/**
         * THE SAME PAN AFFORDANCE THE TABLE WEARS (audit P2-7). The board's complaint is
         * the table's complaint one surface over - a column's cards cut mid-string against
         * a bare strip, with nothing on screen saying a seventh column exists - and one
         * complaint gets one answer. The strips are siblings of the scroller inside this
         * `relative` wrapper, because a strip inside it would pan away with the columns.
         */}
        <div className="relative">
          <div
            ref={boardScroller}
            className="board-cards flex snap-x snap-mandatory gap-(--board-gap) overflow-x-auto pb-4 md:snap-none"
          >
            {columns.map((column) => (
              <KanbanColumn
                key={column.id}
                column={column}
                object={object}
                fields={fields}
                groupField={groupField}
                viewFilter={filter}
                select={select}
                orderBy={orderBy}
                labelField={labelField}
                secondaryFields={secondaryFields}
                count={countByColumn?.get(column.id)}
                onOpen={openRecord}
                onIdsChange={handleIds}
                /**
                 * A create from a column LANDS ON THE RECORD, like every other create on
                 * this screen. The column would show the card - that is what the prefilled
                 * stage is for - but a column pages at 25 rows in label order, so "it is
                 * right there" is a promise the board cannot keep for a full column.
                 */
                onCreated={openRecord}
              />
            ))}
          </div>
          {/* `bottom-4`: the scroller's own `pb-4` is the gap under the columns, and a
              strip that ran into it would shade empty canvas. */}
          <PanEdges start={boardEdges.start} end={boardEdges.end} className="bottom-4" />
        </div>

        {/**
         * THE DRAG IMAGE (wave 5 round 4, B4; spec/screens/list.md section 2 item 12, "rows
         * drag with a lift and drop").
         *
         * THE CAUSE, and it is an INVERSION rather than an omission. Apple's rule is about the
         * thing that TRAVELS: "It works well to create a translucent representation of the
         * content people are dragging", and the reason is stated in the next sentence -
         * "Translucency helps distinguish the representation from the original content and lets
         * people see destinations as they pass over them"
         * (hig: https://developer.apple.com/design/human-interface-guidelines/drag-and-drop,
         * "Providing feedback"). This board did it the other way round: the overlay was opaque
         * with a popover shadow and the card it came from sat at `opacity-40`, so the solid
         * thing under the pointer covered the column it was being aimed at, and the faded thing
         * was the one standing still. A drag read as a duplicated card.
         *
         * SO THE REPRESENTATION IS THE TRANSLUCENT ONE (`.board-card-lift`) and the card it
         * came from keeps its own opacity and merely says it is the source (`[data-dragging]`).
         * Both values are tokens. The destination's own feedback is unchanged and is already
         * Apple's: the column tints only while the card is over it and drops the tint when it
         * leaves ("Display highlighting or other visual cues only while the content is
         * positioned above the destination", same section; `KanbanColumn`'s `isOver`).
         *
         * AND THE DRAG IS NOT THE ONLY PATH. "Offer alternative ways to accomplish
         * drag-and-drop actions" (same page, "Best practices"), which on this board is the
         * card's row menu - see `KanbanCard`.
         *
         * No drop animation: the card is already in its new column, and a card flying back to
         * where it started reads as a failed move.
         *
         * AND THE KEYBOARD DRAG TRAVELS ON THE MOVE FAMILY (wave 5 round 4, M3;
         * `docs/decisions/motion.md` section 2, Move: "a moving part travelling between two
         * resting places"). A pointer drag has no resting places - the image is under the
         * finger and any transition on it is lag - but a KEYBOARD drag is nothing but resting
         * places: each press puts the image somewhere new in one step, and without a
         * transition the card teleports.
         *
         * THE CAUSE, AND IT IS A LITERAL THE MOTION LAYER CANNOT REACH. dnd-kit already knew
         * this and answered it in its own numbers: `@dnd-kit/core@6.3.1`'s `defaultTransition`
         * returns `'transform 250ms ease'` when the activator is a keyboard event
         * (`dist/core.cjs.development.js:3632-3635`, read in this worktree's `node_modules`).
         * 250ms and `ease` happen to equal `--duration-system` and `--ease-system` today, which
         * is exactly what makes it dangerous: it LOOKS right, and the reduced-motion block in
         * `tokens.css` cannot touch it - a reader with Reduce Motion on still gets 250ms of
         * travel on the one drag path that is theirs. So the library's getter is emptied and
         * the family carries it, on `--duration-travel`, the token Reduce Motion sets to zero.
         *
         * THE CLASS IS ON THE OVERLAY'S OWN WRAPPER, which is the element dnd-kit writes the
         * transform onto: `DragOverlay` forwards `className` to `PositionedOverlay`, whose
         * inline `style` sets `transform` and (until now) `transition`. An inline transition
         * outranks a class, which is why the getter has to be emptied rather than left alone.
         */}
        <DragOverlay
          dropAnimation={null}
          transition={() => undefined}
          {...(keyboardDrag ? { className: MOTION.move } : {})}
        >
          {activeRecordId !== null && (
            <div className="board-card-lift w-[85vw] rounded-control border border-line-strong bg-surface md:w-(--board-column-width)">
              <DraggedCard
                objectId={object.id}
                recordId={activeRecordId}
                labelField={labelField}
                secondaryFields={secondaryFields}
                tone={draggedTone(activeRecordId)}
              />
            </div>
          )}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

/** Referential stability for the ids a column reports; a new array every render would loop. */
function sameIds(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

/**
 * The overlay's content, read from the canonical entry like every other card. Its own
 * component because it needs a hook and the overlay renders conditionally.
 */
function DraggedCard({
  objectId,
  recordId,
  labelField,
  secondaryFields,
  tone,
}: {
  readonly objectId: string;
  readonly recordId: string;
  readonly labelField: FieldMetadata | undefined;
  readonly secondaryFields: readonly FieldMetadata[];
  readonly tone: TagTone | null;
}) {
  const record = useRecord(objectId, recordId);
  if (record.data === undefined || record.data === null) {
    return <Skeleton shape="block" className="h-16" />;
  }
  return (
    <KanbanCardBody
      objectId={objectId}
      record={record.data}
      labelField={labelField}
      secondaryFields={secondaryFields}
      tone={tone}
    />
  );
}
